import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { resolveGallery, resolveGalleryAlbums } from './siteGallery';
import { resolveImpactStats } from './siteImpactStats';
import { resolveBrandTicker } from './siteBrandTicker';
import { resolveAgriIntelligence } from './siteAgriIntelligence';
import { resolveSustainableFarming } from './siteSustainableFarming';
import { resolveSiteSeo } from './siteSeo';
import { resolveHeader, resolveSeedVarietyCategories } from './siteHeader';
import DocumentHead from './DocumentHead';
import { resolveHero } from './siteHero';
import { resolveAbout } from './siteAbout';
import { resolveFounderDesk } from './siteFounderDesk';
import { PRODUCTS } from './products';
import { sanitizeProductDescription } from './productHtml';
import { stripNewsHtmlToPlain } from './newsHtml';
import { displayNewsDate, isValidIsoDate } from './isoDateDisplay';
import { apiUrl } from './apiBase';
import { useHomepageAnimations } from './useHomepageAnimations';
import MagneticButton from './components/MagneticButton';
import HeaderGlassOverlay from './components/HeaderGlassOverlay';
import SiteFooter from './components/SiteFooter';

function LeafIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 22C12 22 4 18 4 10C4 6 7 3 11 3C11 3 12 8 12 10C12 8 13 3 13 3C17 3 20 6 20 10C20 18 12 22 12 22Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDown({ className }) {
  return (
    <svg className={className} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Responsive column count for the Crops (seed categories) mega menu. */
function seedCategoryMegaMenuColClass(count) {
  if (count <= 8) return 'grid-cols-2';
  if (count <= 15) return 'grid-cols-3';
  if (count <= 24) return 'grid-cols-4';
  return 'grid-cols-5';
}

function ChevronLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18L9 12L15 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 18L15 12L9 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 12L12 4M12 4H6M12 4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Phone icon centered in 0 0 100 100; stroke coordinates around center */
function IconPhoneHandset() {
  return (
    <g
      transform="translate(50,52)"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M-10 -14c0-3 2.5-6 6-6h2c3.5 0 6 2.5 6 6v28c0 3.5-2.5 6-6 6h-2c-3.5 0-6-2.5-6-6V-14z" />
      <path d="M-4 18h4" />
    </g>
  );
}

function IconEnvelope() {
  return (
    <g
      transform="translate(50,52)"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="-16" y="-11" width="32" height="22" rx="2.5" />
      <path d="M-16 -11l16 14 16-14" />
    </g>
  );
}

function FooterContactScallop({ children }) {
  return (
    <svg
      className="h-11 w-11 shrink-0 sm:h-12 sm:w-12"
      viewBox="0 0 100 100"
      aria-hidden
    >
      <path className="fill-brand-lime" d={SCALLOPED_BG} />
      <g className="text-white">{children}</g>
    </svg>
  );
}

function SearchIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M20 20L16.5 16.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MenuIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 5V3M16 5V3M4 9h16M6 5h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** In-app routes use `Link`; hash links stay as `<a>`. */
function PrimaryNavLink({ href, className, children, onClick, 'aria-current': ariaCurrent, ...rest }) {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className} onClick={onClick} aria-current={ariaCurrent} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick} aria-current={ariaCurrent} {...rest}>
      {children}
    </a>
  );
}

/** 8-lobe scalloped circle, viewBox 0 0 100 100 */
const SCALLOPED_BG =
  'M50 3L63.78 16.74L83.23 16.77L83.26 36.22L97 50L83.26 63.78L83.23 83.23L63.78 83.26L50 97L36.22 83.26L16.77 83.23L16.74 63.78L3 50L16.74 36.22L16.77 16.77L36.22 16.74 Z';

function IconHayBale() {
  return (
    <g
      transform="translate(50,52)"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="0" cy="-1" rx="17" ry="12" />
      <path d="M-15 -1 Q0 -13 15 -1" />
      <path d="M-13 3 Q0 10 13 3" />
      <path d="M-11 -6 H11" />
    </g>
  );
}

function IconCorn() {
  return (
    <g
      transform="translate(50,50)"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M0 -21 C9 -12 11 6 7 20 H-7 C-11 6 -9 -12 0 -21Z" />
      <path d="M-5 -14 H5 M-6 -6 H6 M-6 2 H6 M-5 10 H5" />
      <path d="M-3 -21 Q-8 -24 -10 -18" />
      <path d="M4 -20 Q10 -22 12 -16" />
    </g>
  );
}

function IconSprout() {
  return (
    <g
      transform="translate(50,54)"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M0 16 V-2" />
      <path d="M0 2 Q-18 -6 -20 -20" />
      <path d="M0 0 Q18 -8 20 -22" />
      <path d="M0 6 Q-14 2 -16 -10" />
    </g>
  );
}

function FeatureScallop({ children }) {
  return (
    <svg
      className="mx-auto h-20 w-20 shrink-0 min-[400px]:h-[5.25rem] min-[400px]:w-[5.25rem] sm:h-[6rem] sm:w-[6rem]"
      viewBox="0 0 100 100"
      aria-hidden
    >
      <path className="fill-brand-lime" d={SCALLOPED_BG} />
      <g className="text-white">{children}</g>
    </svg>
  );
}

const HERO_HIGHLIGHT_ICON = {
  hayBale: IconHayBale,
  corn: IconCorn,
  sprout: IconSprout,
};

const AGRO_MAIN_IMG =
  'https://agrezen.zozothemes.com/wp-content/uploads/2025/10/service-img-1-1074x1536.webp';
const AGRO_CARD_ORGANIC =
  'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=800&q=80';
const AGRO_CARD_GREENHOUSE =
  'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=800&q=80';
const AGRO_CARD_SUPPLY =
  'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80';

const agroFeatureCards = [
  {
    title: 'Organic Farm Solutions',
    image: AGRO_CARD_ORGANIC,
    alt: 'Farmer caring for trees in an orchard',
  },
  {
    title: 'The Eco-Friendly Farming',
    image: AGRO_CARD_GREENHOUSE,
    alt: 'Agricultural work inside a greenhouse',
  },
  {
    title: 'Organic Produce Supply',
    image: AGRO_CARD_SUPPLY,
    alt: 'Fresh vegetables and produce ready for distribution',
  },
];

const agroStats = [
  {
    title: '80% Pure Growth',
    text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore.',
    Icon: StatIconSunField,
  },
  {
    title: '95% Organic Roots',
    text: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo.',
    Icon: StatIconHandsPlant,
  },
];

function StatIconSunField({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="38" cy="14" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M38 5v3M38 20v3M31.6 14h-3M47.4 14h-3M33.2 8.6l-2.1 2.1M42.9 18.3l-2.1 2.1M33.2 19.4l2.1 2.1M42.9 9.7l2.1 2.1"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M4 34c6-4 12-2 18 0s12-4 20-2M4 38h40"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8 34c4-6 10-10 16-12s12 2 14 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StatIconHandsPlant({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M18 30c-2-4-1-9 2-12s9-3 12 1c1 2 2 5 1 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M24 18v8M20 22c2-3 4-4 8-4s6 1 8 4M22 14c2 2 4 3 6 3s4-1 6-3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 34c2-6 7-10 14-10h2c4 0 7 2 9 5M4 38c2-4 6-6 10-6h6c3 0 6 1 8 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M40 34c-2-5-6-8-11-8-2 0-4 .5-5 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SproutLabelIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 22V12M12 12c-2-4-6-6-8-2 2-1 5 0 8 2M12 12c2-4 6-6 8-2-2-1-5 0-8 2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Line icons for About impact stats (stroke, matches reference layout). */
function StatIconFarmersReach({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M10 34c2-8 6-14 14-18M38 34c-2-8-6-14-14-18M14 34c2-6 6-10 10-12M34 34c-2-6-6-10-10-12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 12c0-3 2-6 4-8 2 2 4 5 4 8M24 14v6M20 18h8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 38h32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StatIconTruckDistrict({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M14 10v4M22 10v4M30 10v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M10 18h22l4 6h8v10H10V18z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="16" cy="36" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="34" cy="36" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 36H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StatIconRetailers({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="14" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 38V24h24v14M16 24l4-6h8l4 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M22 30h4v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StatIconProjects({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M10 34V16h20l4 4v14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M14 22h12M14 26h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M26 10l6 4-2 8 6 2-10 6-4-12z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StatIconContractFarmers({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <path
        d="M10 30c4-4 8-6 14-8s10 4 14 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M18 22c2-4 4-6 6-8M30 22c-2-4-4-6-6-8M22 8c2 2 4 2 6 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M24 14v6M20 18h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function StatIconExport({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="22" cy="26" r="12" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M14 20h16M22 14v12M10 26c0-6 5-11 12-11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M32 18h8v8M36 14l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StatIconEmployees({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="14" cy="16" r="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 32c0-4 2.5-6 6-6s6 2 6 6v4H8v-4z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="14" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M16 34v-8c0-4 3-7 8-7s8 3 8 7v8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="34" cy="16" r="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M28 32c0-4 2.5-6 6-6s6 2 6 6v4H28v-4z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const IMPACT_ICON_BY_KEY = {
  farmers: StatIconFarmersReach,
  district: StatIconTruckDistrict,
  distributors: StatIconTruckDistrict,
  retailers: StatIconRetailers,
  projects: StatIconProjects,
  contractFarmers: StatIconContractFarmers,
  export: StatIconExport,
  employees: StatIconEmployees,
};

const HEADER_STICKY_AFTER_PX = 200;

/** News carousel: each slide shows this many article cards (grid row). */
const NEWS_CAROUSEL_ITEMS_PER_SLIDE = 4;

function readInitialHeaderSticky() {
  return typeof window !== 'undefined' && window.scrollY > HEADER_STICKY_AFTER_PX;
}

let scrollTopAudioContext = null;

function getScrollTopAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!scrollTopAudioContext || scrollTopAudioContext.state === 'closed') {
    scrollTopAudioContext = new Ctor();
  }
  return scrollTopAudioContext;
}

/** Rocket-style whoosh on back-to-top (synthesized; no audio file). Loud; runs from click gesture. */
function playScrollToTopLaunchSound({ calm = false } = {}) {
  const ctx = getScrollTopAudioContext();
  if (!ctx) return;
  const boot = ctx.state === 'suspended' ? ctx.resume() : Promise.resolve();
  void boot
    .then(() => {
      const t0 = ctx.currentTime;
      const dur = calm ? 0.2 : 0.36;
      const master = ctx.createGain();
      /* Loud mix: branch gains are substantial; master trims slightly so peaks stay near 0 dBFS. */
      master.gain.value = calm ? 0.88 : 0.62;
      master.connect(ctx.destination);

      const noiseLen = Math.floor(ctx.sampleRate * (calm ? 0.08 : 0.12));
      const buf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < noiseLen; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-(i / noiseLen) * 4.2);
      }
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = buf;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(calm ? 0.42 : 0.58, t0);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t0 + (calm ? 0.07 : 0.11));
      noiseSrc.connect(noiseGain);
      noiseGain.connect(master);
      noiseSrc.start(t0);
      noiseSrc.stop(t0 + 0.13);

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const oscGain = ctx.createGain();
      oscGain.gain.setValueAtTime(0.001, t0);
      oscGain.gain.linearRampToValueAtTime(calm ? 0.38 : 0.55, t0 + 0.018);
      oscGain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
      osc.frequency.setValueAtTime(calm ? 240 : 170, t0);
      osc.frequency.exponentialRampToValueAtTime(calm ? 480 : 680, t0 + (calm ? 0.06 : 0.1));
      osc.frequency.exponentialRampToValueAtTime(calm ? 300 : 400, t0 + dur);
      osc.connect(oscGain);
      oscGain.connect(master);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);

      if (!calm) {
        const rumble = ctx.createOscillator();
        rumble.type = 'triangle';
        const rumbleGain = ctx.createGain();
        rumbleGain.gain.setValueAtTime(0.001, t0);
        rumbleGain.gain.linearRampToValueAtTime(0.44, t0 + 0.025);
        rumbleGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.26);
        rumble.frequency.setValueAtTime(52, t0);
        rumble.frequency.exponentialRampToValueAtTime(34, t0 + 0.26);
        rumble.connect(rumbleGain);
        rumbleGain.connect(master);
        rumble.start(t0);
        rumble.stop(t0 + 0.28);
      }
    })
    .catch(() => {});
}

function ScrollToTopRocketIcon({ className, ...rest }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...rest}
    >
      <path
        fill="currentColor"
        d="M12 2.35L8.45 9.85v4.65L6 17.15v1.2l2.95-1.85H10.15V20.25h3.7V16.5h1.2L18 18.35v-1.2l-2.45-2.65v-4.65L12 2.35z"
      />
      <circle cx="12" cy="10.95" r="1.9" fill="#14261c" fillOpacity={0.32} />
    </svg>
  );
}

/** Fixed circular FAB: rocket only, smooth scroll-to-top + launch motion. */
function ScrollToTopFab({ reducedMotion, concealed }) {
  const [pastThreshold, setPastThreshold] = useState(false);
  const [rocketLaunching, setRocketLaunching] = useState(false);
  const launchResetTimerRef = useRef(null);

  useEffect(() => {
    const onChange = () => {
      setPastThreshold(window.scrollY > 380);
    };
    onChange();
    window.addEventListener('scroll', onChange, { passive: true });
    return () => window.removeEventListener('scroll', onChange);
  }, []);

  useEffect(
    () => () => {
      if (launchResetTimerRef.current) window.clearTimeout(launchResetTimerRef.current);
    },
    []
  );

  const goTop = () => {
    playScrollToTopLaunchSound({ calm: reducedMotion });
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    if (reducedMotion) return;
    if (launchResetTimerRef.current) window.clearTimeout(launchResetTimerRef.current);
    setRocketLaunching(true);
    launchResetTimerRef.current = window.setTimeout(() => {
      setRocketLaunching(false);
      launchResetTimerRef.current = null;
    }, 1100);
  };

  const onRocketAnimEnd = (e) => {
    const raw = (e.animationName || '').replace(/['"]/g, '');
    const name = raw.split(',')[0].trim();
    if (name !== 'scroll-top-rocket') return;
    setRocketLaunching(false);
    if (launchResetTimerRef.current) {
      window.clearTimeout(launchResetTimerRef.current);
      launchResetTimerRef.current = null;
    }
  };

  const show = pastThreshold && !concealed;

  return (
    <button
      type="button"
      onClick={goTop}
      aria-label="Back to top"
      className={`group fixed bottom-[max(1.15rem,env(safe-area-inset-bottom))] right-[max(1.15rem,env(safe-area-inset-right))] z-[110] flex h-[3.75rem] w-[3.75rem] shrink-0 items-center justify-center overflow-visible rounded-full border border-white/18 bg-gradient-to-b from-[#1a3329] via-brand-forest to-[#0f1f17] p-0 text-brand-lime shadow-[0_12px_40px_rgba(0,0,0,0.45),0_0_0_1px_rgba(74,222,128,0.12)_inset,0_1px_0_rgba(255,255,255,0.14)_inset] backdrop-blur-md transition-[opacity,transform,box-shadow,border-color] duration-500 ease-[cubic-bezier(0.34,1.3,0.64,1)] before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-brand-lime/15 before:via-transparent before:to-transparent before:opacity-60 before:transition-opacity hover:border-brand-lime/40 hover:shadow-[0_16px_48px_rgba(74,222,128,0.18),0_12px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(74,222,128,0.2)_inset] hover:before:opacity-100 hover:scale-[1.05] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime/55 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-forest motion-reduce:transition-opacity motion-reduce:before:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100 ${
        show ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-8 opacity-0'
      } ${!reducedMotion && rocketLaunching ? 'animate-scroll-top-fab-shake motion-reduce:animate-none' : ''}`}
    >
      <span
        className="pointer-events-none absolute -inset-3 -z-10 rounded-full bg-brand-lime/25 opacity-70 blur-2xl transition-opacity duration-500 group-hover:opacity-100 motion-reduce:opacity-0"
        aria-hidden
      />
      <span className="pointer-events-none relative z-[1] flex h-9 w-9 items-center justify-center overflow-visible">
        {!reducedMotion ? (
          <>
            {/* Long exhaust plume — ordered outer → inner; hangs below nozzle */}
            <span
              className={`pointer-events-none absolute left-1/2 top-[72%] h-[5.25rem] w-[1.65rem] -translate-x-1/2 origin-top rounded-full bg-gradient-to-b from-transparent via-orange-500 to-red-600 opacity-0 blur-[3px] motion-reduce:hidden ${rocketLaunching ? 'animate-scroll-top-rocket-flame' : ''}`}
              aria-hidden
            />
            <span
              className={`pointer-events-none absolute left-1/2 top-[72%] h-[4.5rem] w-3 -translate-x-1/2 origin-top rounded-full bg-gradient-to-b from-transparent via-amber-300 to-orange-600 opacity-0 blur-[2px] motion-reduce:hidden ${rocketLaunching ? 'animate-scroll-top-rocket-flame' : ''}`}
              style={{ animationDelay: '35ms' }}
              aria-hidden
            />
            <span
              className={`pointer-events-none absolute left-1/2 top-[72%] h-[3.75rem] w-2 -translate-x-1/2 origin-top rounded-full bg-gradient-to-b from-transparent via-brand-lime to-brand-lime-hover opacity-0 blur-[1px] motion-reduce:hidden ${rocketLaunching ? 'animate-scroll-top-rocket-flame' : ''}`}
              style={{ animationDelay: '70ms' }}
              aria-hidden
            />
            <ScrollToTopRocketIcon
              className={`relative h-7 w-7 text-brand-lime drop-shadow-[0_2px_10px_rgba(74,222,128,0.55)] transition-transform duration-300 ease-out group-hover:-translate-y-1 motion-reduce:transition-none ${rocketLaunching ? 'animate-scroll-top-rocket' : ''}`}
              aria-hidden
              onAnimationEnd={onRocketAnimEnd}
            />
          </>
        ) : (
          <ChevronLeft className="relative h-7 w-7 translate-y-px rotate-90 text-brand-lime" aria-hidden />
        )}
      </span>
    </button>
  );
}

function App() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [headerSticky, setHeaderSticky] = useState(readInitialHeaderSticky);
  const [headerStickyReveal, setHeaderStickyReveal] = useState(readInitialHeaderSticky);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const prevHeaderStickyRef = useRef(readInitialHeaderSticky());
  const [heroSlide, setHeroSlide] = useState(0);
  const [heroCarouselPaused, setHeroCarouselPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [contactErr, setContactErr] = useState('');
  const [newsCarouselPage, setNewsCarouselPage] = useState(0);
  const newsCarouselTouchX = useRef(null);
  const [openDesktopNavDropdownIndex, setOpenDesktopNavDropdownIndex] = useState(null);
  const [openMobileSeedDropdownIndex, setOpenMobileSeedDropdownIndex] = useState(null);
  const desktopPrimaryNavRef = useRef(null);
  const desktopNavListRef = useRef(null);
  const headerBrandRef = useRef(null);
  const headerToolsRef = useRef(null);
  const headerLogoImgRef = useRef(null);
  const heroSectionRef = useRef(null);
  const heroBgParallaxRef = useRef(null);
  const heroGradientParallaxRef = useRef(null);
  const heroDecorRef = useRef(null);
  const heroCopyRef = useRef(null);
  const heroHighlightsRef = useRef(null);
  const heroTabsRef = useRef(null);
  const desktopNavHoverCloseTimerRef = useRef(null);

  function clearDesktopNavHoverCloseTimer() {
    if (desktopNavHoverCloseTimerRef.current) {
      clearTimeout(desktopNavHoverCloseTimerRef.current);
      desktopNavHoverCloseTimerRef.current = null;
    }
  }

  function openDesktopNavDropdown(idx) {
    clearDesktopNavHoverCloseTimer();
    setOpenDesktopNavDropdownIndex(idx);
  }

  function scheduleCloseDesktopNavDropdown(idx) {
    if (prefersReducedMotion) return;
    clearDesktopNavHoverCloseTimer();
    desktopNavHoverCloseTimerRef.current = setTimeout(() => {
      desktopNavHoverCloseTimerRef.current = null;
      setOpenDesktopNavDropdownIndex((open) => (open === idx ? null : open));
    }, 180);
  }

  const hero = useMemo(() => resolveHero(site), [site]);
  const about = useMemo(() => resolveAbout(site), [site]);
  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const seedVarietyCategories = useMemo(() => resolveSeedVarietyCategories(site), [site]);
  const heroActiveSlide = hero.backgroundSlides[heroSlide] || {};

  useHomepageAnimations({
    reducedMotion: prefersReducedMotion,
    heroSlide,
    headerEl: headerRef,
    brandEl: headerBrandRef,
    headerToolsEl: headerToolsRef,
    navListEl: desktopNavListRef,
    logoImgEl: headerLogoImgRef,
    heroSectionEl: heroSectionRef,
    heroBgEl: heroBgParallaxRef,
    heroGradientEl: heroGradientParallaxRef,
    heroDecorEl: heroDecorRef,
    heroCopyRootEl: heroCopyRef,
    heroHighlightsEl: heroHighlightsRef,
    heroTabsEl: heroTabsRef,
  });

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHeaderSticky(window.scrollY > HEADER_STICKY_AFTER_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!headerSticky) {
      setHeaderStickyReveal(false);
      prevHeaderStickyRef.current = false;
      return;
    }
    if (prefersReducedMotion) {
      setHeaderStickyReveal(true);
      prevHeaderStickyRef.current = true;
      return;
    }
    if (!prevHeaderStickyRef.current) {
      setHeaderStickyReveal(false);
      let innerId;
      const outerId = requestAnimationFrame(() => {
        innerId = requestAnimationFrame(() => setHeaderStickyReveal(true));
      });
      prevHeaderStickyRef.current = true;
      return () => {
        cancelAnimationFrame(outerId);
        if (innerId != null) cancelAnimationFrame(innerId);
      };
    }
    setHeaderStickyReveal(true);
  }, [headerSticky, prefersReducedMotion]);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (openDesktopNavDropdownIndex === null) return;
    const clearDesktopNavDropdown = () => {
      clearDesktopNavHoverCloseTimer();
      setOpenDesktopNavDropdownIndex(null);
    };
    const onDocPointer = (e) => {
      const el = desktopPrimaryNavRef.current;
      if (el && !el.contains(e.target)) {
        clearDesktopNavDropdown();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') clearDesktopNavDropdown();
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDesktopNavDropdownIndex]);

  useEffect(() => {
    if (!mobileNavOpen) setOpenMobileSeedDropdownIndex(null);
  }, [mobileNavOpen]);

  useEffect(
    () => () => {
      if (desktopNavHoverCloseTimerRef.current) {
        clearTimeout(desktopNavHoverCloseTimerRef.current);
        desktopNavHoverCloseTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (mq.matches) setMobileNavOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => { });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onVis = () => setHeroCarouselPaused(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || heroCarouselPaused) return undefined;
    const slides = hero.backgroundSlides;
    const len = slides.length;
    if (len < 2) return undefined;
    const active = slides[heroSlide];
    const ms =
      active?.slideIntervalMs ??
      hero.slideIntervalMs ??
      6500;
    const id = window.setTimeout(() => {
      setHeroSlide((i) => (i + 1) % len);
    }, ms);
    return () => clearTimeout(id);
  }, [
    prefersReducedMotion,
    heroCarouselPaused,
    heroSlide,
    hero.slideIntervalMs,
    hero.backgroundSlides,
  ]);

  const newsChunks = useMemo(() => {
    const list = site.news.articles;
    if (!list.length) return [];
    const out = [];
    for (let i = 0; i < list.length; i += NEWS_CAROUSEL_ITEMS_PER_SLIDE) {
      out.push(list.slice(i, i + NEWS_CAROUSEL_ITEMS_PER_SLIDE));
    }
    return out;
  }, [site.news.articles]);

  const productSection = useMemo(() => {
    const p = site.products;
    const fallback = DEFAULT_SITE.products;
    const label = (v, d) => v || d || 'Our products';
    const title = (v, d) => v || d || 'Seed & planting lines for every season';
    const introText = (v, d) =>
      v ||
      d ||
      'Featured varieties and packs—each dated for traceability. Reach out for availability and agronomic fit in your area.';
    if (p && typeof p === 'object' && Array.isArray(p.items)) {
      return {
        sectionLabel: label(p.sectionLabel, fallback?.sectionLabel),
        sectionTitle: title(p.sectionTitle, fallback?.sectionTitle),
        intro: introText(p.intro, fallback?.intro),
        items: p.items,
      };
    }
    return {
      sectionLabel: label(undefined, fallback?.sectionLabel),
      sectionTitle: title(undefined, fallback?.sectionTitle),
      intro: introText(undefined, fallback?.intro),
      items: PRODUCTS,
    };
  }, [site.products]);

  const gallerySection = useMemo(() => resolveGallery(site), [site.gallery]);
  const galleryAlbums = useMemo(() => resolveGalleryAlbums(site), [site.gallery]);

  const impactStatsRows = useMemo(() => resolveImpactStats(site), [site.impactStats]);

  const brandTickerNames = useMemo(() => resolveBrandTicker(site), [site.brandTicker]);

  const agriIntel = useMemo(() => resolveAgriIntelligence(site), [site]);

  const sustainableFarmingSection = useMemo(() => resolveSustainableFarming(site), [site]);
  const founderDesk = useMemo(() => resolveFounderDesk(site), [site]);

  const siteSeo = useMemo(() => resolveSiteSeo(site), [site]);

  useEffect(() => {
    const n = hero.backgroundSlides.length;
    if (n < 1) return;
    setHeroSlide((i) => i % n);
  }, [hero.backgroundSlides.length]);

  useEffect(() => {
    const len = site.news.articles.length;
    const pages = len === 0 ? 0 : Math.ceil(len / NEWS_CAROUSEL_ITEMS_PER_SLIDE);
    setNewsCarouselPage((p) => {
      if (pages === 0) return 0;
      return p >= pages ? 0 : p;
    });
  }, [site.news.articles]);

  const newsPageCount = newsChunks.length;
  function updateContactField(field, value) {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  }

  async function submitContactForm(e) {
    e.preventDefault();
    setContactMsg('');
    setContactErr('');
    const name = contactForm.name.trim();
    const email = contactForm.email.trim();
    const subject = contactForm.subject.trim();
    const message = contactForm.message.trim();
    if (name.length < 2) {
      setContactErr('Please enter your name.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setContactErr('Please enter a valid email address.');
      return;
    }
    if (subject.length < 3) {
      setContactErr('Please enter a subject (minimum 3 characters).');
      return;
    }
    if (message.length < 10) {
      setContactErr('Please write a short message (minimum 10 characters).');
      return;
    }
    setContactSending(true);
    try {
      const r = await fetch(apiUrl('/api/contact-messages'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone: contactForm.phone,
          subject,
          message,
          sourcePath: typeof window !== 'undefined' ? window.location.pathname : '/',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Message send failed');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setContactMsg('Thank you. Your message has been sent successfully.');
    } catch (err) {
      setContactErr(err instanceof Error ? err.message : 'Could not send your message right now.');
    } finally {
      setContactSending(false);
    }
  }

  const newsGoPage = (delta) => {
    if (newsPageCount < 2) return;
    setNewsCarouselPage((p) => (p + delta + newsPageCount) % newsPageCount);
  };
  const onNewsCarouselTouchStart = (e) => {
    newsCarouselTouchX.current = e.touches[0].clientX;
  };
  const onNewsCarouselTouchEnd = (e) => {
    const start = newsCarouselTouchX.current;
    newsCarouselTouchX.current = null;
    if (start == null || newsPageCount < 2) return;
    const end = e.changedTouches[0].clientX;
    const diff = end - start;
    if (diff > 56) newsGoPage(-1);
    else if (diff < -56) newsGoPage(1);
  };

  return (
    <>
      <DocumentHead seo={siteSeo} />
      <div className="min-h-full">
        {/* Elevate hero + header above later sections so the fixed bar stays on top when scrolling */}
        <div className="relative z-50">
          <section
            ref={heroSectionRef}
            className="relative isolate z-0 flex min-h-[108svh] flex-col overflow-visible bg-brand-intro text-[#f4f7f4] lg:min-h-[52rem]"
            aria-label="Welcome"
            role="region"
            aria-roledescription="carousel"
          >
            <div
              ref={heroBgParallaxRef}
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-neutral-900 will-change-transform"
            >
              {hero.backgroundSlides.map((slide, i) => {
                const src = slide.src;
                const active = i === heroSlide;
                return (
                  <div
                    key={`${i}-${src}`}
                    className={`absolute inset-0 transition-[opacity] duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${active ? 'z-[1] opacity-100' : 'z-0 opacity-0'
                      }`}
                    aria-hidden={!active}
                  >
                    <div
                      key={active ? heroSlide : `off-${i}`}
                      className={`h-full w-full bg-cover bg-bottom bg-no-repeat ${active && !prefersReducedMotion
                          ? 'animate-hero-kenburns will-change-transform motion-reduce:animate-none'
                          : ''
                        }`}
                      style={{ backgroundImage: `url(${src})` }}
                    />
                  </div>
                );
              })}
            </div>
            <div
              ref={heroGradientParallaxRef}
              className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-[#0d1f16]/88 via-[#0d1f16]/45 to-[#0a1812]/92 sm:from-[#0d1f16]/82 sm:via-[#0d1f16]/38 sm:to-[#0a1812]/88 will-change-[opacity]"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-1/3 bg-gradient-to-t from-[#0a1812]/90 to-transparent sm:h-2/5 lg:h-1/3"
              aria-hidden
            />
            <div
              ref={heroDecorRef}
              className="pointer-events-none absolute inset-0 z-[1] overflow-hidden"
              aria-hidden
            >
              <div className="absolute -left-[12%] top-[16%] h-[min(42vw,280px)] w-[min(42vw,280px)] rounded-full bg-brand-lime/[0.14] blur-3xl" />
              <div className="absolute -right-[8%] top-[38%] h-[min(48vw,320px)] w-[min(48vw,320px)] rounded-full bg-emerald-200/[0.09] blur-3xl" />
              <div className="absolute bottom-[22%] left-[18%] h-40 w-40 rounded-full bg-white/[0.06] blur-2xl sm:h-52 sm:w-52" />
            </div>

            {headerSticky ? (
              <div
                aria-hidden
                className="shrink-0"
                style={{ height: Math.max(headerHeight, 1) }}
              />
            ) : null}
            <header
              ref={headerRef}
              className={`pt-[max(0.75rem,env(safe-area-inset-top))] will-change-transform motion-reduce:transition-none ${headerSticky
                  ? `fixed left-0 right-0 top-0 z-[100] bg-transparent shadow-none backdrop-blur-none transition-[transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      prefersReducedMotion || headerStickyReveal
                        ? 'pointer-events-auto translate-y-0'
                        : '-translate-y-full pointer-events-none'
                    }`
                  : 'relative z-[40] translate-y-0 border-0 bg-transparent shadow-none backdrop-blur-none'
                }`}
            >
              <div className="mx-auto max-w-shell px-4 pb-2 sm:px-5 sm:pb-3 md:px-6">
                <div
                  className={`relative isolate overflow-visible rounded-full border px-2.5 py-1.5 shadow-[0_8px_40px_rgba(0,0,0,0.18)] backdrop-blur-2xl transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 sm:px-4 sm:py-2 md:px-5 ${
                    headerSticky
                      ? 'border-neutral-200/70 bg-white/82 ring-1 ring-black/[0.05]'
                      : 'border-white/35 bg-white/12 ring-1 ring-white/25 supports-[backdrop-filter]:bg-white/10'
                  }`}
                >
                  <HeaderGlassOverlay sticky={headerSticky} />
                  <div className="relative z-[1] flex min-h-[3.25rem] items-center justify-between gap-2 sm:min-h-[3.5rem] sm:gap-3">
                  <a
                    ref={headerBrandRef}
                    href={headerSection.brandHref}
                    className="inline-flex min-w-0 max-w-[calc(100%-8rem)] items-center gap-2 text-inherit no-underline sm:max-w-none sm:gap-2.5 md:gap-3"
                  >
                    <img
                      ref={headerLogoImgRef}
                      src={headerSection.logoUrl}
                      alt={headerSection.logoAlt}
                      className={`h-9 w-9 shrink-0 rounded-full object-cover object-center ring-1 will-change-transform sm:h-10 sm:w-10 md:h-11 md:w-11 ${headerSticky ? 'ring-neutral-200/80' : 'ring-white/35'
                        }`}
                      width={120}
                      height={120}
                      decoding="async"
                    />
                    <span className="flex min-w-0 flex-col leading-[1.1] min-[380px]:flex-row min-[380px]:items-baseline min-[380px]:gap-1.5 sm:gap-2">
                      <span
                        className={`truncate text-[1.05rem] font-bold tracking-tight sm:text-[1.2rem] md:text-[1.28rem] ${headerSticky ? 'text-brand-forest' : 'text-white'
                          }`}
                      >
                        {headerSection.brandPrimary}
                      </span>
                      <span
                        className={`truncate text-[1.05rem] font-semibold tracking-tight sm:text-[1.2rem] md:text-[1.28rem] ${headerSticky ? 'text-brand-forest/85' : 'text-[#eef6b8]'
                          }`}
                      >
                        {headerSection.brandSecondary}
                      </span>
                    </span>
                  </a>

                  <nav
                    ref={desktopPrimaryNavRef}
                    className="hidden flex-1 justify-center px-2 lg:flex xl:px-4"
                    aria-label="Primary"
                  >
                    <ul
                      ref={desktopNavListRef}
                      className="m-0 flex list-none flex-wrap items-center justify-center gap-x-0.5 gap-y-1 sm:gap-x-1 xl:gap-x-2"
                    >
                      {headerSection.navLinks.map((link, idx) => {
                        const { label, href, current, dropdown, dropdownSource, dropdownItems = [] } = link;
                        const isSeedCategoryDropdown =
                          Boolean(dropdown) &&
                          dropdownSource === 'seedCategories' &&
                          seedVarietyCategories.length > 0;
                        const isCustomDropdown =
                          Boolean(dropdown) &&
                          dropdownSource === 'custom' &&
                          Array.isArray(dropdownItems) &&
                          dropdownItems.length > 0;
                        const navBase =
                          'inline-flex items-center gap-0.5 text-sm font-medium no-underline transition-[background-color,color,box-shadow] duration-200 xl:text-[0.95rem]';
                        const navHero = current
                          ? 'rounded-full bg-white/22 px-3 py-2 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
                          : 'rounded-full px-3 py-2 text-white/90 hover:bg-white/15 hover:text-white';
                        const navSticky = current
                          ? 'rounded-full bg-brand-forest/12 px-3 py-2 font-semibold text-brand-forest'
                          : 'rounded-full px-3 py-2 text-neutral-600 hover:bg-neutral-100/95 hover:text-brand-forest';
                        const navLinkClass = `${navBase} ${headerSticky ? navSticky : navHero}`;
                        const submenuSurface = headerSticky
                          ? 'border border-neutral-200/90 bg-white/95 shadow-lg backdrop-blur-xl'
                          : 'border border-white/15 bg-gradient-to-b from-[#0a1812]/88 via-[#0a1812]/72 to-[#0a1812]/90 backdrop-blur-2xl shadow-xl shadow-black/35';
                        const submenuItem = headerSticky
                          ? 'text-neutral-800 hover:bg-neutral-50'
                          : 'text-white/[0.92] hover:bg-white/10';
                        const cropsMegaSurface = headerSticky
                          ? 'border border-neutral-200/90 bg-white/95 shadow-lg backdrop-blur-xl'
                          : 'border border-white/30 bg-white/75 shadow-xl shadow-black/25 backdrop-blur-2xl';
                        const cropsMegaItem = headerSticky
                          ? 'text-neutral-700 hover:bg-neutral-50 hover:text-brand-forest'
                          : 'text-neutral-700 hover:bg-white/50 hover:text-brand-forest';
                        const ddOpen = openDesktopNavDropdownIndex === idx;
                        const submenuId = `header-nav-submenu-${idx}`;

                        return (
                          <li
                            key={`${label}-${href}-${idx}`}
                            className={isSeedCategoryDropdown ? 'relative' : 'relative'}
                          >
                            {isSeedCategoryDropdown ? (
                              <div className="relative">
                                <button
                                  type="button"
                                  className={`${navLinkClass} cursor-pointer border-0 bg-transparent font-inherit`}
                                  aria-current={current ? 'page' : undefined}
                                  aria-haspopup="true"
                                  aria-expanded={ddOpen}
                                  aria-controls={submenuId}
                                  id={`header-nav-dd-trigger-${idx}`}
                                  onMouseEnter={() => {
                                    if (!prefersReducedMotion) openDesktopNavDropdown(idx);
                                  }}
                                  onMouseLeave={() => scheduleCloseDesktopNavDropdown(idx)}
                                  onClick={() =>
                                    setOpenDesktopNavDropdownIndex((prev) => (prev === idx ? null : idx))
                                  }
                                >
                                  {label}
                                  <ChevronDown
                                    className={`mt-px h-2.5 w-2.5 opacity-75 transition-transform duration-200 ${ddOpen ? 'rotate-180' : ''
                                      }`}
                                  />
                                </button>
                                {ddOpen ? (
                                  <ul
                                    id={submenuId}
                                    role="menu"
                                    aria-label={`${label} submenu`}
                                    onMouseEnter={() => openDesktopNavDropdown(idx)}
                                    onMouseLeave={() => scheduleCloseDesktopNavDropdown(idx)}
                                    className="absolute left-1/2 top-[130%] z-[10001] m-0 w-max max-w-[min(calc(100vw-2rem),56rem)] -translate-x-1/2 list-none pt-2"
                                  >
                                    <li
                                      role="presentation"
                                      className={`m-0 grid list-none gap-x-3 gap-y-0.5 rounded-2xl px-4 py-4 sm:gap-x-4 sm:px-5 sm:py-5 ${cropsMegaSurface} ${seedCategoryMegaMenuColClass(
                                        seedVarietyCategories.length
                                      )}`}
                                    >
                                      {seedVarietyCategories.map((c) => (
                                        <Link
                                          key={c.id}
                                          role="menuitem"
                                          to={`/crops/${encodeURIComponent(c.id)}`}
                                          className={`block min-w-0 truncate rounded-md px-1.5 py-1.5 text-[0.9rem] font-medium leading-snug no-underline transition-colors ${cropsMegaItem}`}
                                          onClick={() => setOpenDesktopNavDropdownIndex(null)}
                                        >
                                          {c.name}
                                        </Link>
                                      ))}
                                    </li>
                                  </ul>
                                ) : null}
                              </div>
                            ) : isCustomDropdown ? (
                              <div
                                className="relative"
                                onMouseEnter={() => {
                                  if (prefersReducedMotion) return;
                                  if (desktopNavHoverCloseTimerRef.current) {
                                    clearTimeout(desktopNavHoverCloseTimerRef.current);
                                    desktopNavHoverCloseTimerRef.current = null;
                                  }
                                  setOpenDesktopNavDropdownIndex(idx);
                                }}
                                onMouseLeave={() => {
                                  if (prefersReducedMotion) return;
                                  if (desktopNavHoverCloseTimerRef.current) {
                                    clearTimeout(desktopNavHoverCloseTimerRef.current);
                                  }
                                  const leaveIdx = idx;
                                  desktopNavHoverCloseTimerRef.current = setTimeout(() => {
                                    desktopNavHoverCloseTimerRef.current = null;
                                    setOpenDesktopNavDropdownIndex((open) => (open === leaveIdx ? null : open));
                                  }, 160);
                                }}
                              >
                                <button
                                  type="button"
                                  className={`${navLinkClass} cursor-pointer border-0 bg-transparent font-inherit`}
                                  aria-current={current ? 'page' : undefined}
                                  aria-haspopup="true"
                                  aria-expanded={ddOpen}
                                  aria-controls={submenuId}
                                  id={`header-nav-dd-trigger-${idx}`}
                                  onClick={() =>
                                    setOpenDesktopNavDropdownIndex((prev) => (prev === idx ? null : idx))
                                  }
                                >
                                  {label}
                                  <ChevronDown
                                    className={`mt-px h-2.5 w-2.5 opacity-75 transition-transform duration-200 ${ddOpen ? 'rotate-180' : ''
                                      }`}
                                  />
                                </button>
                                <ul
                                  id={submenuId}
                                  role="menu"
                                  aria-label={`${label} submenu`}
                                  hidden={!ddOpen}
                                  className={`absolute left-1/2 top-full z-[10001] mt-1.5 min-w-[13rem] -translate-x-1/2 rounded-xl py-1.5 pt-2.5 ${submenuSurface}`}
                                >
                                  {dropdownItems.map((item, j) => (
                                    <li key={`${item.label}-${item.href}-${j}`} role="none">
                                      <PrimaryNavLink
                                        role="menuitem"
                                        href={item.href}
                                        className={`block px-4 py-2 text-sm font-medium no-underline transition-colors ${submenuItem}`}
                                        onClick={() => setOpenDesktopNavDropdownIndex(null)}
                                      >
                                        {item.label}
                                      </PrimaryNavLink>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <PrimaryNavLink
                                href={href}
                                className={navLinkClass}
                                aria-current={current ? 'page' : undefined}
                              >
                                {label}
                                {dropdown ? (
                                  <ChevronDown className="mt-px h-2.5 w-2.5 opacity-75" />
                                ) : null}
                              </PrimaryNavLink>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </nav>

                  <div ref={headerToolsRef} className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
                    <button
                      type="button"
                      className={`inline-flex h-10 min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-full border-0 p-0 transition-colors sm:h-11 ${headerSticky
                          ? 'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200/90'
                          : 'text-white hover:bg-white/18 active:bg-white/24'
                        }`}
                      aria-label="Search"
                    >
                      <SearchIcon className="h-5 w-5" />
                    </button>
                    <div className="hidden sm:flex sm:shrink-0 sm:items-center">
                      <MagneticButton
                        href="#contact"
                        label="Get In Touch"
                        reducedMotion={prefersReducedMotion}
                        variant={headerSticky ? 'light' : 'hero'}
                        surface={headerSticky ? 'light' : 'dark'}
                        size="sm"
                        className="inline-flex max-w-none scale-[0.94] sm:scale-100"
                      />
                    </div>
                    <button
                      type="button"
                      className={`inline-flex h-10 min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-full border-0 p-0 transition-colors sm:h-11 lg:hidden ${headerSticky
                          ? 'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200/90'
                          : 'text-white hover:bg-white/18 active:bg-white/24'
                        }`}
                      aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
                      aria-expanded={mobileNavOpen}
                      aria-controls="mobile-primary-nav"
                      onClick={() => setMobileNavOpen((o) => !o)}
                    >
                      {mobileNavOpen ? (
                        <CloseIcon className="h-6 w-6" />
                      ) : (
                        <MenuIcon className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            </header>

            <div className="relative z-[10] mx-auto box-border flex w-full max-w-shell flex-1 flex-col justify-center px-4 pb-[max(12rem,env(safe-area-inset-bottom)+11rem)] pt-8 min-[400px]:px-5 sm:px-6 sm:pb-[max(11.5rem,env(safe-area-inset-bottom)+10.5rem)] sm:pt-12 md:pt-16 lg:pb-[max(11rem,env(safe-area-inset-bottom)+10rem)] lg:pt-20">
              <div
                ref={heroCopyRef}
                key={heroSlide}
                className="mx-auto w-full max-w-[38rem] text-left min-[480px]:mx-0"
              >
                <p
                  data-hero-label
                  className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.6875rem] font-semibold uppercase tracking-[0.05em] text-white/[0.95] min-[400px]:text-xs min-[400px]:tracking-[0.06em] sm:mb-4"
                >
                  <LeafIcon className="h-4 w-4 shrink-0 text-brand-lime min-[400px]:h-[1.1rem] min-[400px]:w-[1.1rem]" />
                  {heroActiveSlide.label}
                </p>
                <h1
                  className="mb-4 text-[clamp(1.625rem,5.5vw+0.65rem,3.25rem)] font-bold leading-[1.06] tracking-[-0.03em] text-white text-balance sm:mb-[1.1rem] sm:leading-[1.08]"
                  aria-label={heroActiveSlide.title}
                >
                  <span data-hero-title className="block" aria-hidden="true">
                    {heroActiveSlide.title}
                  </span>
                </h1>
                <p
                  data-hero-subtitle
                  className="mb-6 max-w-lg text-[0.9375rem] leading-relaxed text-white/[0.85] min-[400px]:text-base sm:mb-7 sm:text-base md:text-lg md:leading-relaxed"
                >
                  {heroActiveSlide.subtitle}
                </p>
                <MagneticButton
                  heroCta
                  href={heroActiveSlide.ctaHref}
                  label={heroActiveSlide.ctaLabel}
                  icon={<ArrowUpRight className="h-4 w-4 shrink-0" />}
                  reducedMotion={prefersReducedMotion}
                  variant="hero"
                  surface="dark"
                  size="md"
                  className="mt-1 inline-flex w-full min-h-[48px] items-center justify-center sm:w-auto sm:min-h-0"
                />
              </div>

              <div
                ref={heroTabsRef}
                className="mx-auto mt-8 flex w-full max-w-[38rem] justify-center gap-2 sm:mt-10 min-[480px]:mx-0"
                role="tablist"
                aria-label="Hero background slides"
              >
                {hero.backgroundSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === heroSlide}
                    aria-label={`Show slide ${i + 1} of ${hero.backgroundSlides.length}`}
                    className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-forest/50 ${i === heroSlide ? 'w-8 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/75'
                      }`}
                    onClick={() => setHeroSlide(i)}
                  />
                ))}
              </div>

              <ul
                ref={heroHighlightsRef}
                id="explore"
                className="m-0 mt-10 grid w-full max-w-3xl scroll-mt-24 list-none grid-cols-1 justify-items-center gap-8 min-[400px]:mt-12 min-[400px]:gap-9 sm:mx-auto sm:mt-16 sm:scroll-mt-28 sm:grid-cols-3 sm:items-end sm:gap-6 sm:px-2 md:mt-20 md:max-w-4xl md:gap-10 lg:max-w-5xl lg:gap-16"
                aria-label="Highlights"
              >
                {hero.highlights.map(({ line1, line2, icon }, index) => {
                  const IconCmp = HERO_HIGHLIGHT_ICON[icon] || IconHayBale;
                  return (
                    <li
                      key={`${line1}-${line2}-${index}`}
                      className={`flex w-full max-w-[11rem] flex-col items-center text-center sm:max-w-none ${index === 1 ? 'sm:-translate-y-5 md:-translate-y-6' : ''
                        }`}
                    >
                      <FeatureScallop>
                        <IconCmp />
                      </FeatureScallop>
                      <p className="mt-5 text-sm font-semibold leading-snug text-white sm:text-[0.95rem]">
                        <span className="block">{line1}</span>
                        <span className="block">{line2}</span>
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Impact stats — bottom card, constrained width, white surface */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-[-120px] z-[99] flex justify-center px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5 md:px-6"
              role="region"
              aria-label="Organization impact at a glance"
            >
              <div className="pointer-events-auto w-full max-w-shell rounded-2xl border border-neutral-200/90 bg-white px-3 py-4 [box-shadow:0_8px_20px_-6px_rgba(20,38,28,0.07),0_20px_48px_-14px_rgba(20,38,28,0.14)] sm:px-5 sm:py-5 md:px-6 md:py-6">
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-4 sm:gap-x-4 sm:gap-y-5 md:gap-x-5 lg:gap-x-6">
                  {impactStatsRows.map(({ value, label, icon }, idx) => {
                    const Icon = IMPACT_ICON_BY_KEY[icon] || StatIconFarmersReach;
                    return (
                      <div key={`${idx}-${label}`} className="flex min-w-0 items-start gap-2.5 sm:gap-3">
                        <Icon className="h-8 w-8 shrink-0 text-brand-forest sm:h-9 sm:w-9" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[0.9375rem] font-bold leading-none tracking-tight text-neutral-900 tabular-nums sm:text-base md:text-lg">
                            {value}
                          </p>
                          <p className="mt-1 text-[0.6875rem] leading-snug text-neutral-500 sm:text-xs md:text-[0.8125rem]">
                            {label}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Mobile drawer: outside hero section so fixed overlay is not clipped by overflow-hidden */}
        <div
          className={`fixed inset-0 z-[9000] lg:hidden ${mobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
          aria-hidden={!mobileNavOpen}
        >
          <button
            type="button"
            tabIndex={mobileNavOpen ? 0 : -1}
            className={`absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ease-out motion-reduce:transition-none ${mobileNavOpen ? 'opacity-100' : 'opacity-0'
              }`}
            aria-label="Close navigation menu"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside
            id="mobile-primary-nav"
            className={`absolute inset-y-0 right-0 flex h-full w-[min(20rem,calc(100vw-1rem))] max-w-[100vw] flex-col border-l border-white/15 bg-brand-forest/98 shadow-[-8px_0_32px_rgba(0,0,0,0.35)] backdrop-blur-md transition-transform duration-300 ease-out motion-reduce:transition-none ${mobileNavOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            inert={!mobileNavOpen ? true : undefined}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-4">
              <span className="text-sm font-semibold uppercase tracking-[0.12em] text-white/90">Menu</span>
              <button
                type="button"
                className="inline-flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border-0 bg-white/[0.08] p-0 text-white transition-colors hover:bg-white/[0.14]"
                aria-label="Close menu"
                onClick={() => setMobileNavOpen(false)}
              >
                <CloseIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3" aria-label="Mobile primary">
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {headerSection.navLinks.map((link, idx) => {
                  const { label, href, current, dropdown, dropdownSource, dropdownItems = [] } = link;
                  const isSeedCategoryDropdown =
                    Boolean(dropdown) &&
                    dropdownSource === 'seedCategories' &&
                    seedVarietyCategories.length > 0;
                  const isCustomDropdown =
                    Boolean(dropdown) &&
                    dropdownSource === 'custom' &&
                    Array.isArray(dropdownItems) &&
                    dropdownItems.length > 0;
                  const mobileDdOpen = openMobileSeedDropdownIndex === idx;
                  const mobileSubmenuId = `mobile-nav-submenu-${idx}`;
                  return (
                    <li key={`${label}-${href}-${idx}`}>
                      {isSeedCategoryDropdown ? (
                        <>
                          <button
                            type="button"
                            className={`flex w-full min-h-[48px] items-center justify-between gap-2 rounded-xl border-0 bg-transparent px-3 py-3 text-left text-base font-medium text-inherit transition-colors active:bg-white/10 ${current ? 'text-brand-lime' : 'text-white/[0.95]'
                              }`}
                            aria-expanded={mobileDdOpen}
                            aria-controls={mobileSubmenuId}
                            id={`mobile-nav-dd-trigger-${idx}`}
                            onClick={() =>
                              setOpenMobileSeedDropdownIndex((prev) => (prev === idx ? null : idx))
                            }
                          >
                            <span>{label}</span>
                            <ChevronDown
                              className={`h-3 w-3 shrink-0 opacity-70 transition-transform duration-200 ${mobileDdOpen ? 'rotate-180' : ''
                                }`}
                            />
                          </button>
                          <ul
                            id={mobileSubmenuId}
                            role="menu"
                            hidden={!mobileDdOpen}
                            className="m-0 mb-1 mt-0.5 grid list-none grid-cols-2 gap-x-3 gap-y-0.5 border-l border-white/15 py-1 pl-3"
                          >
                            {seedVarietyCategories.map((c) => (
                              <li key={c.id} role="none" className="min-w-0">
                                <Link
                                  role="menuitem"
                                  to={`/crops/${encodeURIComponent(c.id)}`}
                                  className="flex min-h-[40px] items-center truncate rounded-lg px-2 py-1.5 text-sm font-medium text-white/85 no-underline transition-colors hover:bg-white/10 hover:text-white"
                                  onClick={() => setMobileNavOpen(false)}
                                >
                                  {c.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : isCustomDropdown ? (
                        <>
                          <button
                            type="button"
                            className={`flex w-full min-h-[48px] items-center justify-between gap-2 rounded-xl border-0 bg-transparent px-3 py-3 text-left text-base font-medium text-inherit transition-colors active:bg-white/10 ${current ? 'text-brand-lime' : 'text-white/[0.95]'
                              }`}
                            aria-expanded={mobileDdOpen}
                            aria-controls={mobileSubmenuId}
                            id={`mobile-nav-dd-trigger-${idx}`}
                            onClick={() =>
                              setOpenMobileSeedDropdownIndex((prev) => (prev === idx ? null : idx))
                            }
                          >
                            <span>{label}</span>
                            <ChevronDown
                              className={`h-3 w-3 shrink-0 opacity-70 transition-transform duration-200 ${mobileDdOpen ? 'rotate-180' : ''
                                }`}
                            />
                          </button>
                          <ul
                            id={mobileSubmenuId}
                            role="menu"
                            hidden={!mobileDdOpen}
                            className="m-0 mb-1 mt-0.5 list-none space-y-0.5 border-l border-white/15 py-1 pl-3"
                          >
                            {dropdownItems.map((item, j) => (
                              <li key={`${item.label}-${item.href}-${j}`} role="none">
                                <PrimaryNavLink
                                  role="menuitem"
                                  href={item.href}
                                  className="flex min-h-[44px] items-center rounded-lg px-2 py-2 text-sm font-medium text-white/85 no-underline transition-colors hover:bg-white/10 hover:text-white"
                                  onClick={() => setMobileNavOpen(false)}
                                >
                                  {item.label}
                                </PrimaryNavLink>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <PrimaryNavLink
                          href={href}
                          className={`flex min-h-[48px] items-center justify-between gap-2 rounded-xl px-3 py-3 text-base font-medium no-underline transition-colors active:bg-white/10 ${current ? 'text-brand-lime' : 'text-white/[0.95]'
                            }`}
                          aria-current={current ? 'page' : undefined}
                          onClick={() => setMobileNavOpen(false)}
                        >
                          <span>{label}</span>
                          {dropdown ? (
                            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
                          ) : null}
                        </PrimaryNavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>
        </div>

        <section
          id="about"
          className="relative border-y border-neutral-200/70 bg-gradient-to-b from-white via-[#f4faf6] to-brand-cream/40 py-16 text-neutral-900 md:py-24"
          aria-labelledby="founders-desk-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8 pt-[100px]">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-stretch lg:gap-10 xl:gap-14">
              <div className="relative flex items-center justify-center lg:col-span-5 lg:justify-start">
                <div
                  className="pointer-events-none absolute left-1/2 top-4 h-36 w-36 -translate-x-1/2 rounded-full bg-brand-lime/25 blur-3xl sm:h-44 sm:w-44 lg:left-[28%] lg:top-8 lg:translate-x-0"
                  aria-hidden
                />
                <div className="relative mx-auto aspect-square w-[min(100%,18.5rem)] shrink-0 sm:w-80 md:w-[22rem] lg:mx-0">
                  <div
                    className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-brand-lime/50 to-brand-forest/40 opacity-80 blur-sm"
                    aria-hidden
                  />
                  <div className="relative h-full w-full overflow-hidden rounded-full border-4 border-white bg-brand-forest shadow-xl shadow-brand-forest/20 ring-1 ring-brand-forest/15">
                    <img
                      src={founderDesk.portraitUrl}
                      alt={founderDesk.portraitAlt}
                      className="block rounded-full object-cover object-bottom"
                      loading="lazy"
                      style={{width:550, height:550}}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center lg:col-span-7">
                <div className="mb-5 inline-flex max-w-max items-center gap-2 rounded-full border border-brand-lime/40 bg-white/80 px-3 py-1.5 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-brand-forest shadow-sm backdrop-blur-sm sm:text-xs">
                  <LeafIcon className="h-3.5 w-3.5 text-brand-lime" />
                  {founderDesk.sectionLabel}
                </div>
                <h2
                  id="founders-desk-heading"
                  className="text-[clamp(1.5rem,3vw+0.5rem,2.25rem)] font-bold leading-[1.15] tracking-tight text-brand-forest"
                >
                  {founderDesk.name}
                </h2>
                <p className="mt-2 text-sm font-semibold text-brand-olive sm:text-base">
                  {founderDesk.role}
                </p>
                <div className="mt-8 rounded-2xl border border-neutral-200/80 bg-white/60 p-6 shadow-sm sm:p-8">
                  <p className="text-[0.95rem] leading-[1.75] text-neutral-600 sm:text-base">
                    {founderDesk.message1}
                  </p>
                  <p className="mt-4 text-[0.95rem] leading-[1.75] text-neutral-600 sm:text-base">
                    {founderDesk.message2}
                  </p>
                  <p className="mt-4 text-[0.95rem] leading-[1.75] text-neutral-600 sm:text-base">
                    {founderDesk.message3}
                  </p>
                </div>
                <MagneticButton
                  href={founderDesk.ctaHref}
                  label={founderDesk.ctaLabel}
                  icon={<ArrowUpRight className="h-4 w-4 shrink-0" />}
                  reducedMotion={prefersReducedMotion}
                  variant="hero"
                  surface="light"
                  size="md"
                  fullWidth
                  className="mt-8 inline-flex w-full min-h-[48px] items-center justify-center sm:min-h-0"
                />
              </div>
            </div>
          </div>
        </section>

        <section
          id="founders-desk"
          className="bg-white py-16 text-neutral-900 md:py-24"
          aria-labelledby="about-heading"
        >
          <div className="mx-auto max-w-shell px-6">
            <p className="mb-3 flex items-center gap-2 text-sm font-bold text-neutral-900">
              <SproutLabelIcon className="h-8 w-8 shrink-0 text-green-600" />
              {about.label}
            </p>
            <h2
              id="about-heading"
              className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-neutral-900 md:text-5xl"
            >
              {about.headingLine1}
              {about.headingLine2 ? (
                <>
                  <br /> {about.headingLine2}
                </>
              ) : null}
            </h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-neutral-600 md:mt-7 md:text-lg">
              {about.intro}
            </p>

            <div
              className={`mt-10 grid grid-cols-1 gap-6 md:mt-12 md:gap-8 ${about.youtubeVideoId ? 'md:grid-cols-2' : 'md:grid-cols-1'
                }`}
            >
              <div className="relative min-h-[260px] w-full overflow-hidden rounded-2xl bg-neutral-900 md:min-h-[320px] md:rounded-3xl">
                <video
                  className="absolute inset-0 h-full w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={about.videoPosterUrl || undefined}
                  preload="metadata"
                  aria-label={about.videoAriaLabel || 'About us video'}
                >
                  <source src={about.videoUrl} type="video/mp4" />
                </video>
              </div>
              {about.youtubeVideoId ? (
                <div className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200/90 shadow-md shadow-neutral-900/5 md:rounded-3xl">
                  <div className="relative aspect-video w-full bg-neutral-900">
                    <iframe
                      className="absolute inset-0 h-full w-full border-0"
                      src={`https://www.youtube-nocookie.com/embed/${about.youtubeVideoId}?rel=0&modestbranding=1`}
                      title={about.youtubeIframeTitle}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section
          id="sustainable-farming"
          className="relative bg-brand-cream py-16 -z-0  text-neutral-900 md:py-24"
          aria-labelledby="sustainable-heading"
        >
          <div className="mx-auto max-w-shell px-6 sm:px-10 lg:px-16 xl:px-8">
            <div className="mb-10 flex items-center gap-2 md:mb-12">
              <SproutLabelIcon className="h-6 w-6 shrink-0 text-brand-lime md:h-7 md:w-7" />
              <span className="text-base font-bold text-neutral-900 md:text-lg">
                {sustainableFarmingSection.sectionLabel}
              </span>
            </div>

            <div className="mb-10 grid grid-cols-1 items-start gap-8 md:mb-12 md:grid-cols-2 md:gap-12 lg:gap-20">
              <h2
                id="sustainable-heading"
                className="text-4xl font-bold leading-[1.08] tracking-tight text-neutral-900 md:text-5xl lg:text-6xl"
              >
                {sustainableFarmingSection.sectionTitle}
              </h2>
              <p className="text-base leading-relaxed text-neutral-500 md:pt-2 md:text-lg">
                {sustainableFarmingSection.intro}
              </p>
            </div>

            <div className="mb-10 flex justify-center md:mb-14">
              <a
                href={sustainableFarmingSection.ctaHref}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-lime px-8 py-3.5 text-sm font-semibold text-brand-forest no-underline shadow-sm transition-colors hover:bg-brand-lime-hover md:px-10 md:text-base"
              >
                {sustainableFarmingSection.ctaLabel}
                <ArrowUpRight className="h-4 w-4 md:h-[1.125rem] md:w-[1.125rem]" />
              </a>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:items-start md:gap-6 lg:gap-8">
              {sustainableFarmingSection.cards.map(({ image, alt, stat, label, offset }, cardIndex) => (
                <article
                  key={`${label}-${cardIndex}`}
                  className={`relative min-h-[380px] overflow-hidden rounded-[2rem] border border-neutral-200/70 bg-brand-card-soft shadow-sm md:min-h-[440px] ${offset ? 'md:translate-y-12 lg:translate-y-14' : ''
                    }`}
                >
                  <img
                    src={image}
                    alt={alt}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"
                    aria-hidden
                  />
                  <div className="absolute bottom-0 left-0 z-[1] p-6 md:p-8">
                    <p className="text-4xl font-bold leading-none text-white md:text-5xl">{stat}</p>
                    <p className="mt-2 max-w-[12rem] text-sm font-medium leading-snug text-white/90 md:text-base">
                      {label}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="agri-intelligence"
          className="relative overflow-hidden bg-white py-16 text-neutral-900 md:py-24"
          aria-labelledby="agri-intel-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center md:mb-16">
              <p className="mb-4 flex flex-wrap items-center justify-center gap-2.5 text-sm font-bold text-neutral-900">
                <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                {agriIntel.sectionLabel}
              </p>
              <h2
                id="agri-intel-heading"
                className="mx-auto max-w-4xl text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.2]"
              >
                {agriIntel.sectionTitle}
              </h2>
            </div>

            <div className="flex flex-col items-stretch gap-12 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(260px,380px)_minmax(0,1fr)] lg:items-center lg:gap-6 xl:gap-10">
              {/* Left feature */}
              <article className="order-2 flex flex-col lg:order-1 lg:pr-2">
                <div className="mb-5 flex items-center gap-0 sm:gap-3 lg:justify-end">
                  <div
                    className="relative flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full border-2 border-brand-lime/90 bg-brand-lime/25 shadow-sm ring-4 ring-brand-lime/15"
                    aria-hidden
                  >
                    <span className="text-xl font-bold tabular-nums text-brand-forest">
                      {agriIntel.features[0].step}
                    </span>
                  </div>
                  <div
                    className="hidden min-h-0 flex-1 border-t border-dashed border-neutral-300 lg:block lg:max-w-[7rem] xl:max-w-[9rem]"
                    aria-hidden
                  />
                </div>
                <h3 className="mb-3 text-xl font-bold text-neutral-900 lg:text-right">{agriIntel.features[0].title}</h3>
                <p className="mb-6 text-sm leading-relaxed text-neutral-500 lg:text-right md:text-[0.95rem]">
                  {agriIntel.features[0].text}
                </p>
                <MagneticButton
                  href={agriIntel.features[0].ctaHref}
                  label={agriIntel.features[0].ctaLabel}
                  icon={<ArrowUpRight className="h-4 w-4 shrink-0" />}
                  reducedMotion={prefersReducedMotion}
                  variant="hero"
                  surface="light"
                  size="sm"
                  className="inline-flex self-start lg:self-end"
                />
              </article>

              {/* Center infographic */}
              <div className="order-1 flex justify-center lg:order-2">
                <div className="relative w-full max-w-[min(100%,380px)] sm:max-w-[420px]">
                  <img
                    src={agriIntel.centerImageSrc}
                    alt={agriIntel.centerImageAlt}
                    className="h-auto w-full object-contain drop-shadow-sm"
                    loading="lazy"
                    width={800}
                    height={785}
                  />
                </div>
              </div>

              {/* Right feature */}
              <article className="order-3 flex flex-col lg:pl-2">
                <div className="mb-5 flex items-center gap-0 sm:gap-3">
                  <div
                    className="hidden min-h-0 flex-1 border-t border-dashed border-neutral-300 lg:block lg:max-w-[7rem] xl:max-w-[9rem]"
                    aria-hidden
                  />
                  <div
                    className="relative flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full border-2 border-brand-lime/90 bg-brand-lime/25 shadow-sm ring-4 ring-brand-lime/15"
                    aria-hidden
                  >
                    <span className="text-xl font-bold tabular-nums text-brand-forest">
                      {agriIntel.features[1].step}
                    </span>
                  </div>
                </div>
                <h3 className="mb-3 text-xl font-bold text-neutral-900">{agriIntel.features[1].title}</h3>
                <p className="mb-6 text-sm leading-relaxed text-neutral-500 md:text-[0.95rem]">
                  {agriIntel.features[1].text}
                </p>
                <MagneticButton
                  href={agriIntel.features[1].ctaHref}
                  label={agriIntel.features[1].ctaLabel}
                  icon={<ArrowUpRight className="h-4 w-4 shrink-0" />}
                  reducedMotion={prefersReducedMotion}
                  variant="hero"
                  surface="light"
                  size="sm"
                  className="inline-flex self-start"
                />
              </article>
            </div>
          </div>

          {/* Brand ticker — full viewport width (outside max-w-shell) */}
          <div className="mt-16 w-full min-w-0 border-y border-neutral-200/90 bg-brand-cream/60 py-4 md:mt-20">
            <div className="relative w-full overflow-hidden">
              <div className="flex w-max animate-marquee-slow">
                {[...brandTickerNames, ...brandTickerNames].map((name, i) => (
                  <span
                    key={`${name}-${i}`}
                    className="inline-flex shrink-0 items-center gap-3 px-8 text-base font-bold tracking-tight text-brand-forest md:text-lg"
                  >
                    <span className="text-brand-lime" aria-hidden>
                      ◆
                    </span>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* <section
        id="portfolio"
        className="bg-white py-16 text-neutral-900 md:py-24"
        aria-labelledby="agro-excellence-heading"
      >
        <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl shadow-md shadow-neutral-900/5 ring-1 ring-neutral-200/90 sm:rounded-[1.75rem]">
                <img
                  src={AGRO_MAIN_IMG}
                  alt="Farmer working in a green field at sunrise"
                  className="aspect-[4/5] w-full object-cover sm:aspect-[3/4] sm:min-h-[380px] lg:aspect-auto lg:min-h-[min(100%,36rem)] lg:max-h-[40rem]"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="flex flex-col gap-10 lg:col-span-7 lg:gap-11">
              <div>
                <p className="mb-3 flex flex-wrap items-center gap-2.5 text-sm font-bold text-neutral-900">
                  <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                  Agro Excellence
                </p>
                <h2
                  id="agro-excellence-heading"
                  className="max-w-xl text-3xl font-bold leading-[1.12] tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.15]"
                >
                  Building a Greener Farming
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-8 lg:gap-x-10">
                {agroStats.map(({ title, text, Icon }) => (
                  <div key={title}>
                    <Icon className="mb-4 h-11 w-11 shrink-0 text-neutral-800 sm:h-12 sm:w-12" />
                    <h3 className="mb-2 text-lg font-bold text-neutral-900">{title}</h3>
                    <p className="text-sm leading-relaxed text-neutral-500 md:text-[0.95rem]">{text}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                {agroFeatureCards.map(({ title, image, alt }) => (
                  <article
                    key={title}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                      <img
                        src={image}
                        alt={alt}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-1 flex-col px-4 pb-5 pt-4 sm:px-5 sm:pt-5">
                      <h3 className="mb-4 text-base font-bold leading-snug text-neutral-900 sm:text-[1.0625rem]">
                        {title}
                      </h3>
                      <a
                        href="#contact"
                        className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-brand-olive no-underline transition-colors hover:text-brand-lime"
                      >
                        Read More
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

        <section
          id="products"
          className="border-t border-neutral-100 bg-brand-cream/50 py-16 text-neutral-900 md:py-24"
          aria-labelledby="products-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center md:mb-14">
              <p className="mb-4 flex flex-wrap items-center justify-center gap-2.5 text-sm font-bold text-neutral-900">
                <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                {productSection.sectionLabel}
              </p>
              <h2
                id="products-heading"
                className="mx-auto max-w-3xl text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.2]"
              >
                {productSection.sectionTitle}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-neutral-600 md:mt-5 md:text-lg">
                {productSection.intro}
              </p>
            </div>

            <ul className="m-0 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {productSection.items.map(({ id, title, image, alt, description }, index) => {
                const safeDesc = sanitizeProductDescription(description);
                return (
                  <li key={id}>
                    <Link
                      to={`/products/${encodeURIComponent(id)}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white text-inherit no-underline shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                        <img
                          src={image}
                          alt={alt}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          loading={index < 3 ? 'eager' : 'lazy'}
                          decoding="async"
                        />
                      </div>
                      <div className="flex flex-1 flex-col px-5 pb-5 pt-4 sm:px-6 sm:pt-5">
                        <h3 className="text-lg font-bold leading-snug text-neutral-900 group-hover:text-brand-olive sm:text-[1.125rem]">
                          {title}
                        </h3>
                        {safeDesc ? (
                          <div
                            className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-neutral-600 [&_a]:font-semibold [&_a]:text-brand-olive [&_a]:underline [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                            dangerouslySetInnerHTML={{ __html: safeDesc }}
                          />
                        ) : null}
                        <p className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-olive">
                          View details
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <section
          id="gallery"
          className="border-t border-neutral-100 bg-brand-cream/80 py-16 text-neutral-900 md:py-24"
          aria-labelledby="gallery-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center md:mb-14">
              <p className="mb-4 flex flex-wrap items-center justify-center gap-2.5 text-sm font-bold text-neutral-900">
                <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                {gallerySection.sectionLabel}
              </p>
              <h2
                id="gallery-heading"
                className="mx-auto max-w-3xl text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.2]"
              >
                {gallerySection.sectionTitle}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-neutral-600 md:mt-5 md:text-lg">
                {gallerySection.intro}
              </p>
            </div>

            {galleryAlbums.length === 0 ? (
              <p className="text-center text-sm text-neutral-600">No albums to show yet.</p>
            ) : (
              <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 md:grid-cols-3 md:gap-5">
                {galleryAlbums.map(({ title, thumbnail, alt, itemCount }, index) => (
                  <li key={title}>
                    <Link
                      to={`/gallery/album/${encodeURIComponent(title)}`}
                      className="group flex flex-col overflow-hidden rounded-2xl bg-white text-inherit no-underline shadow-sm ring-1 ring-neutral-200/90 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-olive"
                    >
                      <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-neutral-200/60">
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={alt || title || ''}
                            className="h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                            loading={index < 6 ? 'eager' : 'lazy'}
                            decoding="async"
                            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200 px-4 text-center">
                            <span className="text-sm font-semibold text-neutral-500">{title}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-neutral-100/90 bg-brand-cream/50 px-3 py-2.5 text-center sm:py-3">
                        <p className="m-0 text-sm font-semibold leading-snug text-brand-forest">{title}</p>
                        {itemCount > 0 ? (
                          <p className="m-0 mt-1 text-xs text-neutral-500">
                            {itemCount} {itemCount === 1 ? 'photo' : 'photos'}
                          </p>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4 md:mt-12">
              <Link
                to="/gallery"
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-neutral-300 bg-white px-8 py-3.5 text-[0.9375rem] font-semibold text-neutral-800 no-underline shadow-sm transition-colors hover:bg-neutral-50 sm:min-h-0 sm:w-auto"
              >
                View full gallery
                <ArrowUpRight className="ml-1 h-4 w-4 shrink-0" />
              </Link>

            </div>
          </div>
        </section>

        <section
          id="blog"
          className="border-t border-neutral-100 bg-white py-16 text-neutral-900 md:py-24"
          aria-labelledby="latest-news-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center md:mb-16">
              <p className="mb-4 flex flex-wrap items-center justify-center gap-2.5 text-sm font-bold text-neutral-900">
                <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                {site.news.sectionLabel}
              </p>
              <h2
                id="latest-news-heading"
                className="mx-auto max-w-4xl text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.2]"
              >
                {site.news.sectionTitle}
              </h2>
            </div>

            {newsChunks.length > 0 ? (
              <div
                className="relative"
                role="region"
                aria-roledescription="carousel"
                aria-label="Latest news articles"
              >
                <p className="sr-only" aria-live="polite">
                  Slide {newsCarouselPage + 1} of {newsPageCount}, up to {NEWS_CAROUSEL_ITEMS_PER_SLIDE} articles per slide
                </p>

                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none motion-reduce:duration-0"
                    style={{ transform: `translate3d(-${newsCarouselPage * 100}%, 0, 0)` }}
                    onTouchStart={onNewsCarouselTouchStart}
                    onTouchEnd={onNewsCarouselTouchEnd}
                  >
                    {newsChunks.map((chunk, pageIdx) => (
                      <div
                        key={pageIdx}
                        className="w-full shrink-0 px-0 sm:px-0.5"
                        aria-hidden={pageIdx !== newsCarouselPage}
                      >
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-7 lg:grid-cols-4 lg:gap-6 xl:gap-8 lg:items-stretch">
                          {chunk.map(({ id, title, excerpt, image, alt }, cardIdx) => {
                            const globalIdx = pageIdx * NEWS_CAROUSEL_ITEMS_PER_SLIDE + cardIdx;
                            return (
                              <article
                                key={id || title}
                                className="h-full"
                              >
                                <Link
                                  to={`/news/${encodeURIComponent(id)}`}
                                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-sm no-underline transition-shadow hover:shadow-md"
                                >
                                  <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                                    <img
                                      src={image}
                                      alt={alt}
                                      className="h-full w-full object-cover"
                                      loading={globalIdx < NEWS_CAROUSEL_ITEMS_PER_SLIDE ? 'eager' : 'lazy'}
                                    />
                                  </div>
                                  <div className="flex flex-1 flex-col px-4 pb-5 pt-4 sm:px-5 sm:pb-6 sm:pt-5">
                                    <h3 className="mb-2 line-clamp-2 text-base font-bold leading-snug text-neutral-900 sm:mb-3 sm:text-[1.0625rem]">
                                      {title}
                                    </h3>
                                    <p className="mb-4 line-clamp-2 flex-1 text-sm leading-relaxed text-neutral-500 sm:mb-5 sm:text-[0.95rem]">
                                      {stripNewsHtmlToPlain(excerpt || '')}
                                    </p>
                                    <span className="inline-flex items-center gap-1 self-start text-sm font-semibold text-brand-lime transition-colors group-hover:text-brand-lime-hover">
                                      Read More
                                      <span className="text-base leading-none" aria-hidden>
                                        →
                                      </span>
                                    </span>
                                  </div>
                                </Link>
                              </article>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {newsPageCount > 1 ? (
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                    <button
                      type="button"
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:border-brand-lime/50 hover:bg-brand-cream/60 hover:text-brand-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime/60"
                      onClick={() => newsGoPage(-1)}
                      aria-label="Previous news slide"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <div
                      className="flex items-center justify-center gap-2"
                      role="group"
                      aria-label="News carousel slides"
                    >
                      {newsChunks.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Go to slide ${i + 1} of ${newsPageCount}`}
                          aria-current={i === newsCarouselPage ? 'true' : undefined}
                          className={`h-2.5 rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none ${i === newsCarouselPage
                              ? 'w-8 bg-brand-lime'
                              : 'w-2.5 bg-neutral-300 hover:bg-neutral-400'
                            }`}
                          onClick={() => setNewsCarouselPage(i)}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 shadow-sm transition-colors hover:border-brand-lime/50 hover:bg-brand-cream/60 hover:text-brand-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lime/60"
                      onClick={() => newsGoPage(1)}
                      aria-label="Next news slide"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4 md:mt-12">
              <Link
                to="/news"
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-neutral-300 bg-white px-8 py-3.5 text-[0.9375rem] font-semibold text-neutral-800 no-underline shadow-sm transition-colors hover:bg-neutral-50 sm:min-h-0 sm:w-auto"
              >
                View all news
                <ArrowUpRight className="ml-1 h-4 w-4 shrink-0" />
              </Link>
            </div>
          </div>
        </section>

        <section
          id="contact"
          className="border-t border-neutral-200/80 bg-brand-cream/40 py-16 text-neutral-900 md:py-24"
          aria-labelledby="contact-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-5">
                <p className="mb-3 flex items-center gap-2.5 text-sm font-bold text-neutral-900">
                  <SproutLabelIcon className="h-7 w-7 shrink-0 text-brand-lime sm:h-8 sm:w-8" />
                  Contact Us
                </p>
                <h2
                  id="contact-heading"
                  className="max-w-xl text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl"
                >
                  Let's discuss your farming goals.
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-neutral-600 md:text-lg">
                  Reach us directly for seed availability, seasonal recommendations, and distribution support.
                </p>

                <ul className="mt-8 m-0 flex list-none flex-col gap-4 p-0">
                  <li>
                    <a
                      href={site.footer.telHref || '#'}
                      className="group flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-inherit no-underline shadow-sm transition-colors hover:bg-neutral-50"
                    >
                      <FooterContactScallop>
                        <IconPhoneHandset />
                      </FooterContactScallop>
                      <span className="text-sm font-semibold text-neutral-800 md:text-base">
                        {site.footer.phone || 'Call us for consultation'}
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={site.footer.mailtoHref || '#'}
                      className="group flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-inherit no-underline shadow-sm transition-colors hover:bg-neutral-50"
                    >
                      <FooterContactScallop>
                        <IconEnvelope />
                      </FooterContactScallop>
                      <span className="text-sm font-semibold text-neutral-800 md:text-base">
                        {site.footer.email || 'Email us your inquiry'}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-7">
                <form
                  className="rounded-3xl border border-neutral-200/90 bg-white p-5 shadow-sm sm:p-6 md:p-8"
                  onSubmit={submitContactForm}
                  aria-label="Contact form"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-1">
                      <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        value={contactForm.name}
                        onChange={(e) => updateContactField('name', e.target.value)}
                        placeholder="Your full name"
                        className="box-border w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-brand-lime/70"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => updateContactField('email', e.target.value)}
                        placeholder="name@example.com"
                        className="box-border w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-brand-lime/70"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                        Phone (optional)
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={contactForm.phone}
                        onChange={(e) => updateContactField('phone', e.target.value)}
                        placeholder="+880..."
                        className="box-border w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-brand-lime/70"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                        Subject
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        value={contactForm.subject}
                        onChange={(e) => updateContactField('subject', e.target.value)}
                        placeholder="What is this message about?"
                        className="box-border w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-brand-lime/70"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        required
                        value={contactForm.message}
                        onChange={(e) => updateContactField('message', e.target.value)}
                        placeholder="Tell us what you need..."
                        className="box-border w-full resize-y rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-brand-lime/70"
                      />
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <MagneticButton
                      type="submit"
                      disabled={contactSending}
                      label={contactSending ? 'Sending...' : 'Send message'}
                      icon={<ArrowUpRight className="h-4 w-4 shrink-0" />}
                      reducedMotion={prefersReducedMotion}
                      variant="hero"
                      surface="light"
                      size="submit"
                      className="inline-flex min-h-[48px] items-center justify-center gap-2"
                    />
                    <p className="text-sm text-neutral-500">We'll get back to you as soon as possible.</p>
                  </div>
                  {contactMsg ? <p className="mt-3 text-sm font-medium text-green-700">{contactMsg}</p> : null}
                  {contactErr ? <p className="mt-3 text-sm font-medium text-red-600">{contactErr}</p> : null}
                </form>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter site={site} headerSection={headerSection} />
      </div>
      <ScrollToTopFab reducedMotion={prefersReducedMotion} concealed={mobileNavOpen} />
    </>
  );
}

export default App;
